import React from "react";
import { Card } from "@/components/ui/card";
import { Plane } from "lucide-react";
import Divider from "@tripbuilder/cat-experiences/divider";
import { type ActivityItem as ActivityItemType, FlightItem } from "@/types";
import { DateTime } from "luxon";

interface FlightProps {
  flight: FlightItem;
  locationTimeZone: string;
  nextActivity: ActivityItemType | null;
  activities: ActivityItemType[];
  locationName: string;
}

const Flight: React.FC<FlightProps> = ({
  flight,
  locationTimeZone,
  nextActivity,
  activities,
  locationName,
}) => {
  const uniqueAirlines = [
    ...new Set(flight.airline.map((airline) => airline.code)),
  ];

  if (!flight) {
    return null;
  }

  return (
    <div className="activity-item w-full">
      <Card className="border-neutral-200/75 rounded-xl py-3 px-4 w-full hover:shadow transition-all duration-300 ease-in-out my-3">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center space-x-4">
            <Plane className="w-5 h-5 text-neutral-600" />
            <div className="flex flex-col items-start">
              <p className="text-sm text-neutral-700 font-medium mb-0.5">
                <span className="text-neutral-700">
                  {flight.departureLocation.name}
                </span>
                {flight.departureLocation.code && (
                  <span className="text-sm text-neutral-400 font-normal ml-1.5">
                    {flight.departureLocation.code || ""}
                  </span>
                )}
                <span className="text-neutral-700 mx-2">→</span>
                <span className="text-neutral-700">
                  {flight.arrivalLocation.name || ""}
                </span>
                {flight.arrivalLocation.code && (
                  <span className="text-sm text-neutral-400 font-normal ml-1.5">
                    {flight.arrivalLocation.code || ""}
                  </span>
                )}
              </p>
              <p className="text-xs text-neutral-500 font-light">
                {flight.isArriving
                  ? `Arriving at ${DateTime.fromISO(flight.arrivalTime || "")
                      .setZone(locationTimeZone)
                      .toFormat("h:mm a")}`
                  : `Departing at ${DateTime.fromISO(flight.departureTime || "")
                      .setZone(locationTimeZone)
                      .toFormat("h:mm a")}`}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {uniqueAirlines.map((airline, index) => (
              <img
                key={index}
                src={`https://images.kiwi.com/airlines/64x64/${airline}.png`}
                alt={airline}
                className="w-6 h-6 rounded-full"
              />
            ))}
          </div>
        </div>
      </Card>
      {nextActivity && (
        <Divider
          prevItem={flight as ActivityItemType}
          nextItem={nextActivity}
          locationName={locationName}
          dayItems={activities}
        />
      )}
    </div>
  );
};

export default Flight;
